<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="stepTitle"
      [canGoBack]="true"
      [customBackAction]="handleTopNavBack" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  @if (ready) {
  <tidy-wrapper [class.flex-content]="currentStep === 3">
  <!-- Step 2: Meet Your Account Manager (pm-demo-account-manager experiment).
       The account-manager card + CTA render once; the per-design intro (header + context)
       and middle content vary by amDesign (control / salesy-v1 / salesy-v2 / salesy-v3). -->
  @if (currentStep === 2) {
    <!-- Per-design intro: control keeps the simple tagline; the salesy designs get a real
         header + a context line so the cards below make sense. -->
    @if (amDesign === 'salesy-v1') {
      <div class="am-intro">
        <tidy-text [body]="'<b>Everything a property manager does — for 3.9%</b>'" class="am-intro-title" />
        <tidy-text [helper]="'Schedule a quick call and your account manager sets it all up for you.'" class="am-intro-sub" />
      </div>
    } @else if (amDesign === 'salesy-v2') {
      <div class="am-intro">
        <tidy-text [body]="'<b>More profit. Less work.</b>'" class="am-intro-title" />
        <tidy-text [helper]="'Join 100,000+ who switched to TIDY — and never looked back.'" class="am-intro-sub" />
      </div>
    } @else if (amDesign === 'salesy-v3') {
      <div class="am-intro">
        <tidy-text [body]="'<b>Get set up on one quick call</b>'" class="am-intro-title" />
        <tidy-text [helper]="'Your account manager gets your properties live — you keep full control.'" class="am-intro-sub" />
      </div>
    } @else {
      <tidy-row [align]="'center'">
        <tidy-text [helper]="'Please schedule a call with your account manager to get started with TIDY.'" class="gl-sales-tagline" />
      </tidy-row>
    }

    <div class="gl-sales">
      <tidy-card>
        <div class="gl-card">
          <div class="gl-manager-photo-wrap">
            <img [src]="managerPhoto" [alt]="managerName" class="gl-manager-photo" />
          </div>
          <div class="gl-manager-text">
            <span class="gl-manager-eyebrow">Account Manager</span>
            <tidy-text [header]="managerName" />
          </div>
        </div>
      </tidy-card>
    </div>

    <!-- SALESY V1: product value-prop cards in the Go Live style (black card, icon left). -->
    @if (amDesign === 'salesy-v1') {
      <tidy-text [body]="'TIDY handles everything:'" class="am-section-label" />
      <div class="am-cards">
        @for (b of amBenefits; track b.title) {
          <div class="am-card">
            <div class="gl-card-icon-wrap">
              <tidy-image [src]="b.icon" class="gl-card-icon" />
            </div>
            <div class="am-card-body">
              <div class="am-card-title-row">
                <tidy-text [header]="b.title" />
                @if (b.badge) { <span class="am-card-badge">{{ b.badge }}</span> }
              </div>
              <tidy-text class="am-card-desc" [body]="b.body" />
            </div>
          </div>
        }
      </div>
    }

    <!-- SALESY V2: social proof (stats) + savings callout + testimonial. -->
    @if (amDesign === 'salesy-v2') {
      <tidy-text [body]="'Why owners switch:'" class="am-section-label" />
      <div class="am-stats">
        @for (st of amStats; track st.label) {
          <div class="am-stat">
            <div class="am-stat-value">{{ st.value }}</div>
            <div class="am-stat-label">{{ st.label }}</div>
          </div>
        }
      </div>
      <div class="am-cards">
        <div class="am-card am-card-callout">
          <div class="am-card-body">
            <tidy-text [body]="amSavings.title" class="am-callout-title" />
            <tidy-text class="am-card-desc" [body]="amSavings.body" />
          </div>
        </div>
      </div>
    }

    <!-- SALESY V3: setup-call agenda (the profit-guarantee alert renders below the CTA). -->
    @if (amDesign === 'salesy-v3') {
      <tidy-text [body]="'What happens on your setup call:'" class="am-section-label" />
      <div class="am-cards">
        @for (st of amSteps; track st.num) {
          <div class="am-card">
            <div class="am-step-num">{{ st.num }}</div>
            <div class="am-card-body">
              <div class="am-card-title-row">
                <tidy-text [header]="st.title" />
              </div>
              <tidy-text class="am-card-desc" [body]="st.body" />
            </div>
          </div>
        }
      </div>
    }

    <div class="gs-schedule-call-row">
      <tidy-button
        [text]="'Schedule Setup Call'"
        [action]="goToScheduleCall"
        class="primary" />
    </div>

    <!-- SALESY V2: reviews sit below the CTA. -->
    @if (amDesign === 'salesy-v2') {
      <div class="am-cards am-cards-below-cta">
        @for (r of amTestimonials; track r.attribution) {
          <div class="am-card am-card-testimonial">
            <div class="am-card-body">
              <div class="am-stars">★★★★★</div>
              <tidy-text [body]="r.quote" class="am-quote" />
              <tidy-text class="am-card-desc" [body]="r.attribution" />
            </div>
          </div>
        }
      </div>
    }

    <!-- SALESY V3: profit-guarantee alert sits below the CTA. -->
    @if (amDesign === 'salesy-v3') {
      <div class="am-cards am-cards-below-cta">
        <div class="am-card am-card-callout">
          <div class="am-card-body">
            <span class="am-callout-badge">{{ amGuarantee.badge }}</span>
            <tidy-text [body]="amGuarantee.title" class="am-callout-title" />
            <tidy-text class="am-card-desc" [body]="amGuarantee.body" />
          </div>
        </div>
      </div>
    }
  }

  <!-- Step 3: Schedule onboarding call -->
  @if (currentStep === 3) {
    <!-- pm-schedule-call-selection experiment: 'auto-learn' hides the goal radios and drops the
         visitor straight into the Learn-about-TIDY Calendly; 'control' keeps the radio choice. -->
    @if (scheduleSelectionVariant !== 'auto-learn') {
      <tidy-row class="gs-question">
        <tidy-text [fieldLabel]="'What do you want to do on your setup call?'" />
      </tidy-row>
      <div class="gs-radio-list gs-goal-list">
        @for (item of goalOptions; track item.value) {
          <div class="gs-radio-option" (click)="selectGoal(item.value)">
            <span class="gs-radio" [class.gs-radio-selected]="goal === item.value">
              <span class="gs-radio-inner"></span>
            </span>
            <div class="gs-radio-text">
              <tidy-text [body]="item.viewValue" />
              <tidy-text [helper]="item.extraText" />
            </div>
          </div>
        }
      </div>
    }

    <div
      #calendlyContainer
      class="gs-calendly-embed flex-fill">
    </div>
  }

  <!-- Step 1: Create account (sign up only) -->
  @if (currentStep === 1) {
    <div class="gs-social-buttons">
      <button type="button" class="gs-social-button" (click)="signInWithGoogle()" [disabled]="isCreatingAccount">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span>Google</span>
      </button>
      <button type="button" class="gs-social-button" (click)="signInWithApple()" [disabled]="isCreatingAccount">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
        <span>Apple</span>
      </button>
    </div>

    <div class="gs-divider"><span>or</span></div>

    <form [formGroup]="signUpForm">
      <tidy-row>
        <div class="gs-form-row">
          <tidy-input
            formControlName="firstName"
            [form]="signUpForm.controls.firstName"
            [label]="'First Name'"
            [errorMessage]="'Please enter your first name.'"
            [submitted]="submitted"
            [icon]="'assets/svg/person.svg'" />
          <tidy-input
            formControlName="lastName"
            [form]="signUpForm.controls.lastName"
            [label]="'Last Name'"
            [errorMessage]="'Please enter your last name.'"
            [submitted]="submitted"
            [icon]="'assets/svg/person.svg'" />
        </div>
      </tidy-row>
      <!-- pm-create-account-phone experiment: 'show-phone' puts a required phone field to the
           right of email (like last name is right of first name); 'control' = email only. -->
      @if (phoneVariant === 'show-phone') {
        <tidy-row>
          <div class="gs-form-row gs-form-row--stack-mobile">
            <tidy-input
              formControlName="email"
              [form]="signUpForm.controls.email"
              [label]="'Email'"
              type="email"
              [errorMessage]="'Please enter a valid email.'"
              [submitted]="submitted"
              [icon]="'assets/svg/mail.svg'" />
            <tidy-input-international-phone
              formControlName="phone"
              [label]="'Phone'"
              [form]="signUpForm.controls.phone"
              [inputMode]="'numeric'"
              [submitted]="submitted"
              [errorMessage]="'Please enter a valid phone.'"
              [icon]="'assets/svg/phone-portrait.svg'"
              [initialMask]="{ mask: '[00000000000000000000000]' }"
              (countryCodeChange)="onPhoneCountryCodeChange($event)" />
          </div>
        </tidy-row>
      } @else {
        <tidy-row>
          <tidy-input
            formControlName="email"
            [form]="signUpForm.controls.email"
            [label]="'Email'"
            type="email"
            [errorMessage]="'Please enter a valid email.'"
            [submitted]="submitted"
            [icon]="'assets/svg/mail.svg'" />
        </tidy-row>
      }
      <tidy-row>
        <tidy-input
          formControlName="password"
          [form]="signUpForm.controls.password"
          [label]="'Password'"
          type="password"
          [errorMessage]="'Password must be at least 8 characters.'"
          [submitted]="submitted"
          [icon]="'assets/svg/lock-closed.svg'" />
      </tidy-row>
      @if (accountError) {
        <tidy-row>
          <tidy-error-message [text]="accountError" />
        </tidy-row>
      }
      <tidy-row>
        <tidy-button
          [text]="isCreatingAccount ? 'Creating Account...' : 'Create Account'"
          [action]="createAccount"
          [disabled]="isCreatingAccount"
          class="primary full-width" />
      </tidy-row>
      <tidy-row [align]="'center'">
        <tidy-text [helper]="'By creating an account, you agree to our'" /><span>&nbsp;</span>
        <tidy-text [body]="'Terms of Service'" (action)="util.openUrl('https://www.tidy.com/terms')" class="link" /><span>&nbsp;</span>
        <tidy-text [helper]="'and'" /><span>&nbsp;</span>
        <tidy-text [body]="'Privacy Policy'" (action)="util.openUrl('https://www.tidy.com/legal/privacy-policy')" class="link" />
      </tidy-row>
    </form>
  }

  </tidy-wrapper>
  }
</ng-template>

@if (!windowService.isDesktopRes) {
  <app-scroll-wrapper [templateContent]="content" />
} @else {
  <ng-container *ngTemplateOutlet="content" />
}
